import { useAuth } from '@clerk/clerk-react';
import {
  CommunitiesDropDown,
  EndUserEntityBreadcrumb,
  MembersDropDown,
  ProfileButton,
  Search,
  SitesDropDown,
} from '@flexo/atoms';
import {
  ellipsizeMiddle,
  formatDate,
  getTimeGranularityOverview,
  httpService,
  IconWrapper,
} from '@flexo/general';
import { ALERT, store } from '@flexo/reducers';
import { Player } from '@lottiefiles/react-lottie-player';
import { Button, MenuItem, Select } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { HiveCalendar } from '../../hive-calendar/hive-calendar';
import styles from '../header-web.module.scss';
import { HeaderWebProps } from '../types';

const ENV = (import.meta as any).env;
const loader = '/csvDownloadLottie.json';

const {
  Header__Web__Left,
  Header__Web__Right,
  Header__Web__Center,
  Logo,
  Header__Navigation,
  Header__Navigation__Icon,
} = styles;

interface BaseHeaderWebProps extends HeaderWebProps {
  isSelectOpen?: boolean;
  setSelectOpen?: (open: boolean) => void;
  triggerSelectOpen?: () => void;
}

export const BaseHeaderWeb: React.FC<BaseHeaderWebProps> = ({
  allowUserCreation: _allowUserCreation = false,
  breadcrumbs,
  showCalendar = false,
  calendarHidden = false,
  showSearch = false,
  showProfile = false,
  showDownload = false,
  selectOptions,
  currentSelectValue,
  onSelectChange,
  isSelectOpen = false,
  setSelectOpen,
  triggerSelectOpen,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = store.dispatch;
  const [isDownloading, setIsDownloading] = useState(false);
  const requestIdRef = useRef(0);
  const { getToken } = useAuth();
  const currentPath = location.pathname;
  const calendar = useSelector((state: any) => state.calendar);
  const metadata = useSelector((state: any) => state.metadata);
  const params = useParams();
  const entityId = params.id as string;

  const { selectedDate, selectedTimeSet } = calendar.hiveCalendar;

  function getFlexoIds(aggregation: string) {
    if (aggregation === 'member') {
      const sites = ((metadata?.entities?.sites as any) || [])
        ?.filter((site: any) => site?.memberId === entityId)
        .map((site: any) => site?.siteId);

      return sites;
    }
    return [entityId];
  }

  const onDownloadClick = async () => {
    const idToken = await getToken();

    let aggregation = 'site';
    if (currentPath.includes('member-energy')) {
      aggregation = 'member';
    }
    if (currentPath.includes('community-energy')) {
      aggregation = 'community';
    }

    const currentRequestId = ++requestIdRef.current;
    setIsDownloading(true);

    try {
      const response = await httpService.post({
        url: '/custom/kwick/v1/exportDataCsv',
        data: {
          aggregation_level: aggregation === 'member' ? 'site' : aggregation,
          date_from: formatDate(selectedDate[0]),
          date_to: formatDate(selectedDate[1]),
          flexo_ids: getFlexoIds(aggregation),
          time_granularity: getTimeGranularityOverview(selectedTimeSet),
        },
        idToken: idToken,
      });

      // Ignore if a newer request has started (e.g., after navigation)
      if (currentRequestId !== requestIdRef.current) return;

      if (response.status === 200) {
        try {
          const csvText =
            typeof response.data === 'string'
              ? response.data
              : response?.data?.data ?? '';
          const dateFrom = formatDate(selectedDate[0]);
          const dateTo = formatDate(selectedDate[1]);
          const fileName = `${entityId}-export-${aggregation}-${dateFrom}_to_${dateTo}.csv`;

          const blob = new Blob(['\ufeff', csvText], {
            type: 'text/csv;charset=utf-8;',
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (e) {
          dispatch(
            ALERT.SetAlert({
              value: {
                type: 'BANNER_ERROR_WITH_CSV_EXPORT',
              },
            } as any) as any
          );
        }
      } else {
        dispatch(
          ALERT.SetAlert({
            value: {
              type: 'BANNER_ERROR_WITH_CSV_EXPORT',
            },
          } as any) as any
        );
      }
    } catch (e) {
      // Network or unexpected error: show banner unless superseded by a newer request
      if (currentRequestId === requestIdRef.current) {
        dispatch(
          ALERT.SetAlert({
            value: {
              type: 'BANNER_ERROR_WITH_CSV_EXPORT',
            },
          } as any) as any
        );
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsDownloading(false);
      }
    }
  };

  useEffect(() => {
    // Invalidate any in-flight request when path changes
    requestIdRef.current++;
    if (isDownloading === true) {
      setIsDownloading(false);
    }
  }, [currentPath]);

  useEffect(() => {
    // Invalidate any in-flight request when calendar filters change
    requestIdRef.current++;
    if (isDownloading === true) {
      setIsDownloading(false);
    }
  }, [selectedDate, selectedTimeSet]);

  useEffect(() => {
    return () => {
      // Invalidate on unmount to ignore late responses
      requestIdRef.current++;
    };
  }, []);

  return (
    <>
      <div className={Header__Web__Left}>
        <div className={Logo} onClick={() => navigate(ENV.VITE_APP_FIRST_PAGE)}>
          <IconWrapper iconName={'logo'} />
        </div>
        <div className={styles.Logo__MobileBack} onClick={() => window.history.back()}>
          <IconWrapper iconName={'chevron-left'} />
        </div>

        <div className={Header__Navigation}>
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              {breadcrumb.type === 'enduser-entity' ? (
                <div className={styles.Header__Navigation__Breadcrumb}>
                  <EndUserEntityBreadcrumb />
                </div>
              ) : breadcrumb.type === 'communities-dropdown' ? (
                <div className={styles.Header__Navigation__Breadcrumb}>
                  <CommunitiesDropDown showCalendar={showCalendar} />
                </div>
              ) : breadcrumb.type === 'members-dropdown' ? (
                <div className={styles.Header__Navigation__Breadcrumb}>
                  <MembersDropDown showCalendar={showCalendar} />
                </div>
              ) : breadcrumb.type === 'sites-dropdown' ? (
                <div className={styles.Header__Navigation__Breadcrumb}>
                  <SitesDropDown showCalendar={showCalendar} />
                </div>
              ) : breadcrumb.isClickable ? (
                <label
                  className={`${styles.clickable}`}
                  title={breadcrumb.title}
                  onClick={breadcrumb.onClick}
                >
                  {showCalendar
                    ? ellipsizeMiddle(breadcrumb?.label || '')
                    : breadcrumb?.label || ''}
                </label>
              ) : (
                <strong title={breadcrumb.title}>
                  {showCalendar
                    ? ellipsizeMiddle(breadcrumb?.label || '')
                    : breadcrumb?.label || ''}
                </strong>
              )}

              {index < breadcrumbs.length - 1 && (
                <span className={`${Header__Navigation__Icon}`}>
                  <IconWrapper iconName="chevron-right" />
                </span>
              )}
            </React.Fragment>
          ))}

          {selectOptions && currentSelectValue && (
            <>
              <span className={`${Header__Navigation__Icon}`}>
                <IconWrapper iconName="chevron-right" />
              </span>
              <Select
                className="site-navigation-select"
                id="site-navigation-select"
                open={isSelectOpen}
                onOpen={() => setSelectOpen?.(true)}
                onClose={() => setSelectOpen?.(false)}
                MenuProps={{
                  className: 'site-navigation-select-menu',
                  id: 'site-navigation-select-menu',
                }}
                value={currentSelectValue}
                IconComponent={() => (
                  <div onClick={triggerSelectOpen}>
                    <IconWrapper
                      className={'site-navigation-select-icon'}
                      iconName="chevron-down"
                    />
                  </div>
                )}
              >
                {selectOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    disabled={option.disabled}
                    onClick={option.onClick}
                    value={option.value}
                    style={{
                      color:
                        option.value === currentSelectValue
                          ? 'var(--hive-primary-500)'
                          : 'inherit',
                      fontWeight:
                        option.value === currentSelectValue ? 600 : 'normal',
                      opacity:
                        option.value === currentSelectValue ? 1 : 'inherit',
                    }}
                  >
                    {ellipsizeMiddle(option?.label || '')}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        </div>
      </div>

      {(showCalendar ) && (
        <div className={`${Header__Web__Center} `}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          
          <HiveCalendar calendarColor="var(--hive-primary-500)" />
        </div>
      )}

      

      <div className={Header__Web__Right}>
        {showDownload && (
          <div className={`${styles.Header__Web__Download__CSV} `}>
            {isDownloading ? (
              <Player
                autoplay
                loop
                src={loader}
                style={{
                  height: 50,
                  width: 64,
                  marginTop: -19,
                }}
              />
            ) : (
              <Button
                type="button"
                onClick={() => onDownloadClick()}
                aria-label="Download CSV"
              >
                <IconWrapper iconName="download_csv" />
              </Button>
            )}
          </div>
        )}

        {showSearch && <Search />}
        {showProfile && <ProfileButton />}
      </div>

      {calendarHidden && (
        <HiveCalendar calendarColor="" hideInput isAnchorTarget />
      )}
    </>
  );
};
